import {
  AppModuleCode,
  CHANGE_CALENDAR_TIMEZONE,
  CHANGE_RISK_LABELS,
  PermissionCode,
  type ChangeCalendarItem,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { useModule } from '@/features/auth/use-module';
import {
  berlinYearMonth,
  nextBerlinYearMonth,
  selectUpcomingChanges,
} from '@/products/helpdesk/features/changes/change-upcoming';
import { ChangeRiskBadge, StatusBadge } from '@/products/helpdesk/features/tickets/ticket-badges';
import { useChangeCalendar } from '@/products/helpdesk/features/tickets/use-tickets';
import { ApiRequestError } from '@/lib/api';
import { formatDateTimeInZone } from '@/lib/format';

const UPCOMING_DAYS = 7;
const UPCOMING_LIMIT = 5;

export function HomeUpcomingChanges() {
  const me = useMe();
  const changesOn = useModule(AppModuleCode.CHANGES);
  const canSee = changesOn && hasPermission(me.data, PermissionCode.TICKETS_READ_ALL);

  const { year, month } = berlinYearMonth();
  const next = nextBerlinYearMonth(year, month);
  const current = useChangeCalendar({ year, month });
  const following = useChangeCalendar({ year: next.year, month: next.month });

  if (!canSee) {
    return null;
  }

  const isPending = current.isPending || following.isPending;
  const isError = current.isError || following.isError;
  const error = current.error ?? following.error;
  const items = selectUpcomingChanges(
    [...(current.data?.items ?? []), ...(following.data?.items ?? [])],
    { daysAhead: UPCOMING_DAYS, limit: UPCOMING_LIMIT },
  );

  return (
    <Card className="gap-0 py-0 shadow-sm">
      <CardHeader className="border-b py-4">
        <CardTitle className="text-base">
          <h2 className="text-base font-semibold">Kommende Änderungen</h2>
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Nächste {UPCOMING_DAYS} Tage · Zeiten Europe/Berlin
        </p>
        <CardAction>
          <Button asChild variant="outline" size="sm">
            <Link to="/helpdesk/changes">Change-Kalender</Link>
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="px-0 py-0">
        {isPending && (
          <p className="flex items-center gap-2 px-6 py-4 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Changes werden geladen …
          </p>
        )}
        {isError && (
          <p role="alert" className="px-6 py-4 text-sm text-destructive">
            {error instanceof ApiRequestError
              ? error.message
              : 'Die kommenden Changes konnten nicht geladen werden.'}
          </p>
        )}
        {!isPending && !isError && items.length === 0 && (
          <p className="px-6 py-4 text-sm text-muted-foreground">
            In den nächsten {UPCOMING_DAYS} Tagen sind keine Changes geplant.
          </p>
        )}
        {!isPending && !isError && items.length > 0 && (
          <ul className="divide-y">
            {items.map((entry) => (
              <UpcomingRow key={entry.id} item={entry} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function UpcomingRow({ item }: { item: ChangeCalendarItem }) {
  return (
    <li>
      <Link
        to={`/helpdesk/tickets/${item.number}`}
        className="flex flex-col gap-1 px-6 py-3 hover:bg-muted/50 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="min-w-0">
          <p className="truncate font-medium">
            #{item.number} · {item.subject}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {formatDateTimeInZone(item.scheduledStart, CHANGE_CALENDAR_TIMEZONE)}–
            {formatDateTimeInZone(item.scheduledEnd, CHANGE_CALENDAR_TIMEZONE)}
            {item.approved ? '' : ` · ${CHANGE_RISK_LABELS[item.risk]}`}
          </p>
        </div>
        <span className="flex flex-wrap items-center gap-1.5">
          <StatusBadge status={item.status} />
          <ChangeRiskBadge risk={item.risk} />
        </span>
      </Link>
    </li>
  );
}
